import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth, ROLES } from './AuthContext';
import { getAllCities, getSchoolsSimple } from '../services/dashboardService';
import { setCidadeAtiva } from '../services/api';

/**
 * Contexto global de "cidade selecionada" no sidebar.
 *
 * - Admin pode escolher qualquer cidade cadastrada que tenha ao menos 1 escola.
 * - Demais papeis ficam travados na cidade vinculada ao proprio usuario.
 * - A escolha eh persistida em localStorage ("sideb.selectedCityId").
 *
 * Paginas podem consumir via `const { selectedCityId, setSelectedCityId, cities } = useSelectedCity();`
 *
 * A escolha tambem vai para o servico de API (`setCidadeAtiva`), que passa a
 * anexar `?city=` em toda requisicao GET. Sem isso, cada tela precisaria
 * lembrar de repassar a cidade — e quem esquecesse mostraria a rede inteira
 * para um admin que escolheu um municipio.
 */

const STORAGE_KEY = 'sideb.selectedCityId';

const SelectedCityContext = createContext({
  cities: [],
  selectedCityId: null,
  setSelectedCityId: () => {},
  loading: false,
  isAdmin: false,
});

export function SelectedCityProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;

  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCityId, _setSelectedCityId] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  });

  const setSelectedCityId = useCallback((value) => {
    _setSelectedCityId(value);
    try {
      if (value === null || value === undefined || value === '') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, String(value));
      }
    } catch {
      /* ignore storage errors */
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setCities([]);
      return;
    }
    if (!isAdmin) {
      // Usuario nao-admin fica travado na propria cidade
      if (user.city) {
        setCities([{ id: user.city, city: user.city_name || '', state: user.city_state || '' }]);
        _setSelectedCityId(Number(user.city));
      }
      return;
    }
    setLoading(true);
    // Busca cidades e escolas em paralelo, e mantem apenas cidades
    // que tenham ao menos 1 escola cadastrada.
    Promise.all([
      getAllCities(),
      // Sem recorte: e desta lista que sai quais cidades tem escola, ou seja,
      // quais aparecem no seletor. Recortada pela cidade ja escolhida, o
      // seletor passaria a oferecer so ela.
      getSchoolsSimple({ todasAsCidades: true }).catch(() => []),
    ])
      .then(([allCities, schools]) => {
        const cityIdsWithSchools = new Set(
          (Array.isArray(schools) ? schools : [])
            .map((s) => Number(s.id_city))
            .filter((id) => Number.isFinite(id))
        );
        const list = Array.isArray(allCities) ? allCities : [];
        const filtered = list.filter((c) => cityIdsWithSchools.has(Number(c.id)));
        setCities(filtered);

        // Se a cidade atualmente selecionada nao esta na lista filtrada,
        // limpa a selecao para evitar estado invalido.
        if (selectedCityId && !filtered.some((c) => Number(c.id) === Number(selectedCityId))) {
          _setSelectedCityId(null);
          try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar cidades (sidebar):', err);
        setCities([]);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  // Mantem o servico de API a par da cidade escolhida. So o admin recorta por
  // aqui: os demais perfis ja sao recortados no backend pela propria cidade,
  // e mandar o parametro so mascararia um escopo mal aplicado no servidor.
  // Sincronizado durante a renderizacao (e nao num efeito) porque as telas
  // disparam as buscas no primeiro efeito delas, que roda antes do nosso.
  const cidadeParaApi = isAdmin ? (selectedCityId ?? null) : null;
  setCidadeAtiva(cidadeParaApi);
  useEffect(() => () => setCidadeAtiva(null), []);

  return (
    <SelectedCityContext.Provider
      value={{ cities, selectedCityId, setSelectedCityId, loading, isAdmin }}
    >
      {children}
    </SelectedCityContext.Provider>
  );
}

export function useSelectedCity() {
  return useContext(SelectedCityContext);
}

export default SelectedCityContext;
